import { AnimatePresence, motion } from 'framer-motion';
import {
    Check,
    ChevronDown,
    ChevronRight,
    ClipboardList,
    Download,
    HelpCircle,
    RotateCcw,
    X,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Détermine si une valeur est "remplie" (non vide, non nulle).
 */
const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';

/**
 * Retourne un message de guidage contextuel selon la progression.
 */
const getGuidanceMessage = (filledRows, totalRows, filledFields, totalFields) => {
  if (filledRows === 0)
    return 'Commencez par remplir la première ligne — cliquez dessus pour l\'ouvrir.';
  if (filledRows < Math.ceil(totalRows / 2))
    return `Bien démarré. Continuez avec les lignes suivantes (${filledRows}/${totalRows} complétées).`;
  if (filledRows < totalRows)
    return `Encore ${totalRows - filledRows} ligne${totalRows - filledRows > 1 ? 's' : ''} à compléter avant de pouvoir compiler.`;
  return 'Toutes les lignes sont remplies. Vous pouvez compiler le tableau.';
};

// ─────────────────────────────────────────────────────────────────────────────
// FIELD COMPONENT — une cellule de saisie avec label
// ─────────────────────────────────────────────────────────────────────────────
const Field = ({ column, value, onChange, isFirst, autoFocus }) => {
  const inputRef = useRef(null);
  const filled = isFilled(value);

  // Champs longs → textarea, sinon input
  const isLong = column.type === 'long' || column.type === 'textarea';

  return (
    <div className="group/field">
      <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
        <span className={filled ? 'text-emerald-400' : 'text-slate-500'}>
          {column.label || column.name || column}
        </span>
        {isFirst && (
          <span className="text-[9px] normal-case font-normal tracking-normal text-cyan-500/60 ml-1">
            — champ principal
          </span>
        )}
        {filled && <Check size={10} className="text-emerald-400 ml-auto" />}
      </label>

      {isLong ? (
        <textarea
          ref={inputRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          rows={2}
          placeholder={column.placeholder || `Saisissez ${(column.label || column.name || column).toLowerCase()}…`}
          className={`w-full px-3 py-2 text-sm rounded-lg resize-none outline-none transition-all
            bg-white/[0.03] border placeholder:text-slate-700 text-white
            ${filled
              ? 'border-emerald-500/30 focus:border-emerald-500/60'
              : 'border-white/[0.08] focus:border-cyan-500/50 focus:bg-cyan-500/[0.03]'
            }`}
        />
      ) : (
        <input
          ref={inputRef}
          type={column.type === 'number' ? 'number' : 'text'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          placeholder={column.placeholder || `Saisissez ${(column.label || column.name || column).toLowerCase()}…`}
          className={`w-full px-3 py-2 text-sm rounded-lg outline-none transition-all
            bg-white/[0.03] border placeholder:text-slate-700 text-white
            ${filled
              ? 'border-emerald-500/30 focus:border-emerald-500/60'
              : 'border-white/[0.08] focus:border-cyan-500/50 focus:bg-cyan-500/[0.03]'
            }`}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROW CARD — une entrée expandable dans le tableau
// ─────────────────────────────────────────────────────────────────────────────
const RowCard = ({
  rowIndex,
  columns,
  rowData,
  onFieldChange,
  isOpen,
  onToggle,
  rowLabel,
  isNextToFill,
}) => {
  // Normalise les colonnes (string ou objet)
  const normalizedCols = columns.map((c) =>
    typeof c === 'string' ? { name: c, label: c } : c
  );

  const firstCol = normalizedCols[0];
  const otherCols = normalizedCols.slice(1);

  const filledCount = normalizedCols.filter((c) =>
    isFilled(rowData[c.name || c])
  ).length;
  const totalCount = normalizedCols.length;
  const isComplete = filledCount === totalCount;
  const hasAnyValue = filledCount > 0;
  const mainValue = rowData[firstCol.name || firstCol] || '';

  return (
    <motion.div
      layout
      className={`rounded-xl border transition-all duration-200 overflow-hidden ${
        isOpen
          ? 'border-cyan-500/40 bg-cyan-500/[0.04] shadow-lg shadow-cyan-500/5'
          : isComplete
          ? 'border-emerald-500/20 bg-emerald-500/[0.02]'
          : isNextToFill && !hasAnyValue
          ? 'border-cyan-500/20 bg-cyan-500/[0.02]'
          : 'border-white/[0.06] bg-white/[0.01] hover:border-white/[0.12] hover:bg-white/[0.025]'
      }`}
    >
      {/* Row header — always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        {/* Index + status indicator */}
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-bold transition-all ${
            isComplete
              ? 'bg-emerald-500 text-black'
              : isOpen
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
              : hasAnyValue
              ? 'bg-white/10 text-white/60 border border-white/10'
              : 'bg-white/[0.04] text-slate-600 border border-white/[0.06]'
          }`}
        >
          {isComplete ? <Check size={12} /> : rowIndex + 1}
        </div>

        {/* Main value preview */}
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm truncate transition-colors ${
              mainValue ? 'text-white font-medium' : 'text-slate-600 italic'
            }`}
          >
            {mainValue || (rowLabel ? `${rowLabel} ${rowIndex + 1}` : `Entrée ${rowIndex + 1}`)}
          </p>
          {!isOpen && hasAnyValue && !isComplete && (
            <p className="text-[10px] text-slate-600 mt-0.5">
              {filledCount}/{totalCount} champs remplis
            </p>
          )}
        </div>

        {/* Progress pills */}
        <div className="flex items-center gap-2 shrink-0">
          {!isOpen && (
            <div className="flex gap-0.5">
              {normalizedCols.map((c, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    isFilled(rowData[c.name || c])
                      ? 'bg-emerald-500'
                      : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
          )}
          {/* Chevron */}
          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.15 }}>
            <ChevronDown size={14} className={isOpen ? 'text-cyan-400' : 'text-slate-600'} />
          </motion.div>
        </div>
      </button>

      {/* Expanded fields */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div className="px-4 pb-4 pt-1 space-y-4 border-t border-white/[0.06]">
              {/* First column — toujours en premier, identifiant principal */}
              <Field
                column={firstCol}
                value={rowData[firstCol.name || firstCol] || ''}
                onChange={(v) => onFieldChange(firstCol.name || firstCol, v)}
                isFirst
                autoFocus
              />

              {/* Other columns */}
              {otherCols.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {otherCols.map((col, j) => (
                    <Field
                      key={j}
                      column={col}
                      value={rowData[col.name || col] || ''}
                      onChange={(v) => onFieldChange(col.name || col, v)}
                    />
                  ))}
                </div>
              )}

              {/* Completion feedback */}
              <AnimatePresence>
                {isComplete && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20"
                  >
                    <Check size={12} className="text-emerald-400" />
                    <p className="text-[11px] text-emerald-300">Cette entrée est complète.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPILED VIEW — aperçu tableau une fois compilé
// ─────────────────────────────────────────────────────────────────────────────
const CompiledTable = ({ columns, rows, onEdit }) => {
  const normalizedCols = columns.map((c) =>
    typeof c === 'string' ? { name: c, label: c } : c
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <p className="text-xs font-semibold text-emerald-300">Tableau compilé</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20 bg-white/[0.03] text-xs text-slate-400 hover:text-white transition-all"
          >
            <RotateCcw size={12} /> Modifier
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-300 hover:bg-emerald-500/20 transition-all">
            <Download size={12} /> Exporter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden border border-white/[0.08]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] bg-white/[0.03]">
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-8">
                  #
                </th>
                {normalizedCols.map((col, i) => (
                  <th
                    key={i}
                    className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap"
                  >
                    {col.label || col.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.02] ${
                    i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.015]'
                  }`}
                >
                  <td className="px-4 py-3 text-[10px] text-slate-600">{i + 1}</td>
                  {normalizedCols.map((col, j) => (
                    <td key={j} className="px-4 py-3 text-slate-200 text-xs">
                      {row[col.name || col] || (
                        <span className="text-slate-700 italic">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Entrées', value: rows.length },
          { label: 'Colonnes', value: normalizedCols.length },
          {
            label: 'Complétude',
            value: `${Math.round(
              (rows.reduce(
                (acc, row) =>
                  acc +
                  normalizedCols.filter((c) => isFilled(row[c.name || c])).length,
                0
              ) /
                (rows.length * normalizedCols.length)) *
                100
            )}%`,
          },
        ].map((stat, i) => (
          <div
            key={i}
            className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center"
          >
            <p className="text-lg font-semibold text-white">{stat.value}</p>
            <p className="text-[10px] text-slate-600 uppercase tracking-wider mt-0.5">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// GUIDANCE BANNER — réactif, dismissable
// ─────────────────────────────────────────────────────────────────────────────
const GuidanceBanner = ({ message, filledRows, totalRows }) => {
  const [dismissed, setDismissed] = useState(false);
  const [prevMessage, setPrevMessage] = useState(message);

  if (message !== prevMessage) {
    setDismissed(false);
    setPrevMessage(message);
  }

  if (dismissed) return null;

  const isComplete = filledRows === totalRows && totalRows > 0;
  const color = isComplete ? 'emerald' : 'cyan';

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={message}
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.2 }}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border mb-4 ${
          isComplete
            ? 'border-emerald-500/30 bg-emerald-500/[0.08]'
            : 'border-cyan-500/25 bg-cyan-500/[0.06]'
        }`}
      >
        <div
          className={`w-1.5 h-1.5 rounded-full shrink-0 ${
            isComplete ? 'bg-emerald-400' : 'bg-cyan-400 animate-pulse'
          }`}
        />
        <p
          className={`flex-1 text-xs leading-relaxed ${
            isComplete ? 'text-emerald-300' : 'text-cyan-300/80'
          }`}
        >
          {message}
        </p>
        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
        >
          <X size={11} className="text-white/25" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * TableBuilder refondu
 *
 * Props :
 *   config       { columns: Array<string|{name,label,type?,placeholder?}>, rowLabel?: string }
 *   previewData  Array<object>  — données initiales (peut être vide ou pré-rempli)
 *   onCompile    (rows) => void — callback quand l'apprenant compile
 */
const TableBuilder = ({ config = {}, previewData = [], onCompile }) => {
  const columns = config.columns || [];
  const rowLabel = config.rowLabel || null;

  // Initialise les lignes depuis previewData, ou crée N lignes vides selon config
  const initialRowCount = config.rowCount || Math.max(previewData.length, 3);
  const initialRows = useMemo(() => {
    const base = Array.from({ length: initialRowCount }, (_, i) => ({
      ...(previewData[i] || {}),
    }));
    return base;
  }, []); // eslint-disable-line

  const [rows, setRows] = useState(initialRows);
  const [openIndex, setOpenIndex] = useState(0); // première ligne ouverte par défaut
  const [isCompiled, setIsCompiled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Métriques de progression
  const normalizedCols = useMemo(
    () => columns.map((c) => (typeof c === 'string' ? { name: c, label: c } : c)),
    [columns]
  );

  const rowsStats = useMemo(() => {
    return rows.map((row) => ({
      filledFields: normalizedCols.filter((c) => isFilled(row[c.name || c])).length,
      totalFields: normalizedCols.length,
      isComplete: normalizedCols.every((c) => isFilled(row[c.name || c])),
      hasAny: normalizedCols.some((c) => isFilled(row[c.name || c])),
    }));
  }, [rows, normalizedCols]);

  const filledRows = rowsStats.filter((r) => r.isComplete).length;
  const totalRows = rows.length;
  const globalProgress = totalRows > 0 ? Math.round((filledRows / totalRows) * 100) : 0;
  const canCompile = filledRows === totalRows && totalRows > 0;

  // Détermine la prochaine ligne vide pour guider
  const nextToFill = rowsStats.findIndex((r) => !r.hasAny);

  // Mise à jour d'un champ
  const handleFieldChange = useCallback((rowIndex, fieldName, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [fieldName]: value };
      return next;
    });
  }, []);

  // Toggle ouverture d'une ligne, + ouvre auto la suivante quand une ligne est complète
  const handleToggle = useCallback(
    (idx) => {
      setOpenIndex((prev) => {
        if (prev === idx) {
          // Ferme → cherche la prochaine incomplète
          const next = rowsStats.findIndex((r, i) => i > idx && !r.isComplete);
          return next !== -1 ? next : null;
        }
        return idx;
      });
    },
    [rowsStats]
  );

  // Auto-avance vers la ligne suivante dès qu'une ligne est complète
  const prevStats = useRef(rowsStats);
  if (prevStats.current !== rowsStats) {
    const justCompleted = rowsStats.findIndex(
      (r, i) => r.isComplete && !prevStats.current[i]?.isComplete
    );
    if (justCompleted !== -1 && openIndex === justCompleted) {
      const next = rowsStats.findIndex((r, i) => i > justCompleted && !r.isComplete);
      if (next !== -1) {
        // Délai pour laisser le feedback "complète" apparaître
        setTimeout(() => setOpenIndex(next), 600);
      }
    }
    prevStats.current = rowsStats;
  }

  const handleCompile = () => {
    setIsCompiled(true);
    onCompile?.(rows);
  };

  const handleEdit = () => {
    setIsCompiled(false);
  };

  const handleReset = () => {
    if (window.confirm('Effacer toutes les saisies et recommencer ?')) {
      setRows(Array.from({ length: initialRowCount }, () => ({})));
      setOpenIndex(0);
      setIsCompiled(false);
    }
  };

  if (!columns.length) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-600 text-sm">
        Configuration manquante — définissez au moins une colonne.
      </div>
    );
  }

  // ── Vue compilée ──────────────────────────────────────────────────────────
  if (isCompiled) {
    return <CompiledTable columns={columns} rows={rows} onEdit={handleEdit} />;
  }

  // ── Vue saisie ────────────────────────────────────────────────────────────
  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList size={16} className="text-amber-400" />
          <div>
            <h2 className="text-sm font-semibold text-white">
              {config.title || 'Tableau de données'}
            </h2>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {normalizedCols.length} colonne{normalizedCols.length > 1 ? 's' : ''} · {totalRows} entrée{totalRows > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp((v) => !v)}
            className={`p-2 rounded-xl border transition-all ${
              showHelp
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                : 'border-white/10 bg-white/[0.03] text-slate-500 hover:text-white'
            }`}
            title="Aide"
          >
            <HelpCircle size={14} />
          </button>
          <button
            onClick={handleReset}
            className="p-2 rounded-xl border border-white/10 bg-white/[0.03] text-slate-500 hover:text-red-400 hover:border-red-500/30 transition-all"
            title="Réinitialiser"
          >
            <RotateCcw size={14} />
          </button>
        </div>
      </div>

      {/* Help panel — inline, non bloquant */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  label: 'Ouvrir une ligne',
                  desc: 'Cliquez sur une entrée pour la développer et accéder à ses champs.',
                },
                {
                  label: 'Navigation automatique',
                  desc: 'Quand une ligne est complète, la suivante s\'ouvre automatiquement.',
                },
                {
                  label: 'Indicateurs de progression',
                  desc: 'Les petits points colorés montrent quels champs sont remplis dans chaque ligne.',
                },
                {
                  label: 'Compiler',
                  desc: 'Une fois toutes les entrées complètes, compilez pour générer le tableau final.',
                },
              ].map((h, i) => (
                <div key={i} className="flex gap-2.5">
                  <ChevronRight size={12} className="text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-white">{h.label}</p>
                    <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guidance banner */}
      <GuidanceBanner
        message={getGuidanceMessage(filledRows, totalRows, 0, 0)}
        filledRows={filledRows}
        totalRows={totalRows}
      />

      {/* Progress bar globale */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-600">Progression globale</span>
          <span className="text-[10px] font-semibold text-white">
            {filledRows}/{totalRows} entrée{filledRows !== 1 ? 's' : ''} complète{filledRows !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
          <motion.div
            className={`h-full rounded-full transition-colors ${
              canCompile ? 'bg-emerald-500' : 'bg-amber-500'
            }`}
            initial={{ width: 0 }}
            animate={{ width: `${globalProgress}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Column legend — rappel des colonnes attendues */}
      <div className="flex flex-wrap gap-1.5 px-1">
        {normalizedCols.map((col, i) => (
          <span
            key={i}
            className="px-2 py-0.5 rounded-md border border-white/[0.08] bg-white/[0.03] text-[10px] text-slate-500"
          >
            {col.label || col.name}
          </span>
        ))}
      </div>

      {/* Row cards */}
      <div className="space-y-2">
        {rows.map((row, i) => (
          <RowCard
            key={i}
            rowIndex={i}
            columns={columns}
            rowData={row}
            onFieldChange={(fieldName, value) => handleFieldChange(i, fieldName, value)}
            isOpen={openIndex === i}
            onToggle={() => handleToggle(i)}
            rowLabel={rowLabel}
            isNextToFill={i === nextToFill}
          />
        ))}
      </div>

      {/* Compile button */}
      <div className="pt-2">
        <motion.button
          onClick={handleCompile}
          disabled={!canCompile}
          whileHover={canCompile ? { scale: 1.01 } : {}}
          whileTap={canCompile ? { scale: 0.99 } : {}}
          className={`w-full py-3.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
            canCompile
              ? 'bg-emerald-500 hover:bg-emerald-400 text-black shadow-lg shadow-emerald-500/20 cursor-pointer'
              : 'bg-white/[0.04] border border-white/[0.06] text-slate-600 cursor-not-allowed'
          }`}
        >
          {canCompile ? (
            <>
              <Check size={15} />
              Compiler le tableau
            </>
          ) : (
            <>
              <ClipboardList size={15} />
              {totalRows - filledRows} entrée{totalRows - filledRows > 1 ? 's' : ''} à compléter avant de compiler
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default TableBuilder;